import { useCallback } from 'react';
import { Column } from '@elementary/common-ui/components';
import * as atoms from '@elementary/common-ui/atoms';
import {
  AgentCodeTestConfigChange,
  AgentCodeTestConfigPlan,
  AgentCodeTestCreateChange,
  FieldChange,
  getAgentTestChangeRowId,
  isAgentCodeTestCreateChange,
} from '@elementary/common-ui/types';
import { AgentConfigPreviewShell } from '../agentConfigPreviewShared';
import { BREADCRUMB_LABELS, SEVERITY_OPTIONS, STORAGE_KEY } from './constants';
import { CodePreviewRow, CodeTestConfigFieldKey } from './types';

const extractNew = <T,>(field?: FieldChange<T>): T | undefined => field?.new;

const buildCodeCreatePreviewRow = (
  change: AgentCodeTestCreateChange,
): CodePreviewRow => ({
  id: getAgentTestChangeRowId(change),
  asset_id: change.assetId,
  schema: '',
  table: change.resourceName,
  column: change.column,
  package: change.packageName === 'elementary' ? 'elementary' : 'dbt',
  name: change.testType,
  type: change.testType,
  severity: extractNew(change.severity) || 'warn',
  owners: extractNew(change.owners) ?? [],
  tags: extractNew(change.tags) ?? [],
  args: {},
  updated_at: '',
  meta: {},
  is_singular: false,
  original_path: '',
  is_cloud: false,
  channels: [],
  model_name: change.resourceName,
  source_name: change.sourceName,
  configChange: change,
  isCreateRow: true,
});

const getPresentFields = (
  plan: AgentCodeTestConfigPlan,
): Set<CodeTestConfigFieldKey> => {
  const fields = new Set<CodeTestConfigFieldKey>();
  if (plan.operation === 'delete') return fields;

  for (const change of plan.changes) {
    if (change.severity) fields.add('severity');
    if (change.tags) fields.add('tags');
    if (change.owners) fields.add('owners');
    if (change.description) fields.add('description');
    if (change.params && Object.keys(change.params).length > 0) {
      fields.add('params');
    }
  }
  return fields;
};

const CodeTestAgentConfigPreview = () => {
  const buildDynamicColumns = useCallback(
    ({
      setChange,
      presentFields,
      ownerOptions,
      tagOptions,
      columnBuilders,
    }: Parameters<
      React.ComponentProps<
        typeof AgentConfigPreviewShell<
          AgentCodeTestConfigChange,
          CodePreviewRow
        >
      >['buildDynamicColumns']
    >[0]) => {
      const columns: Column<CodePreviewRow>[] = [];

      if (presentFields.has('params')) {
        columns.push(
          columnBuilders.buildParamsColumn(
            'code_params_change',
            'Parameters',
            (row) => row.configChange.params,
          ),
        );
      }
      if (presentFields.has('severity')) {
        columns.push(
          columnBuilders.buildDropdownColumn(
            'code_severity_change',
            'Severity',
            SEVERITY_OPTIONS,
            (row) => row.configChange.severity,
            (row, newValue) =>
              setChange(row.id, (change) => {
                const field = change.severity;
                if (!field) return change;
                return {
                  ...change,
                  severity: { ...field, new: newValue },
                };
              }),
          ),
        );
      }
      if (presentFields.has('tags')) {
        columns.push(
          columnBuilders.buildListColumn(
            'code_tags_change',
            'Tags',
            ['tag', 'tags'],
            (row) => row.configChange.tags,
            (row, newValue) =>
              setChange(row.id, (change) => {
                const field = change.tags;
                if (!field) return change;
                return {
                  ...change,
                  tags: { ...field, new: newValue },
                };
              }),
            tagOptions,
            { isElasticLayout: true },
          ),
        );
      }
      if (presentFields.has('owners')) {
        columns.push(
          columnBuilders.buildListColumn(
            'code_owners_change',
            'Owners',
            ['owner', 'owners'],
            (row) => row.configChange.owners,
            (row, newValue) =>
              setChange(row.id, (change) => {
                const field = change.owners;
                if (!field) return change;
                return {
                  ...change,
                  owners: { ...field, new: newValue },
                };
              }),
            ownerOptions,
            { isElasticLayout: true },
          ),
        );
      }
      return columns;
    },
    [],
  );

  return (
    <AgentConfigPreviewShell
      planAtom={atoms.agentCodeTestConfigPlan}
      isCreateChange={isAgentCodeTestCreateChange}
      buildCreatePreviewRow={(change) => {
        if (!isAgentCodeTestCreateChange(change)) {
          throw new Error('Expected create change');
        }
        return buildCodeCreatePreviewRow(change);
      }}
      isCreateRowDataLoading={false}
      getPresentFields={getPresentFields}
      buildDynamicColumns={buildDynamicColumns}
      getSubmitLabel={() => 'Open PR'}
      breadcrumbLabels={BREADCRUMB_LABELS}
      storageKey={STORAGE_KEY}
    />
  );
};

export default CodeTestAgentConfigPreview;
